import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { getFormValues, reset } from 'redux-form';
import { isEmpty } from 'lodash';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { FormName } from '@topconagriculture/feed-types';
import { checkPermissions } from '@topconagriculture/casbin-react';
import { Action, Resource } from '@topconagriculture/permissions';

import * as actions from './settings.actions';
import { LoadersNames } from '../../ui/ui.constants';
import api from '../../../core/api';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { getFarmsListRequest } from '../farms/farms.actions';
import { Forms } from '../../form/form.selector';
import { timeZone as timeZoneConstant } from '../../../config/constants';
import { farmsSelector } from '../../selectors';
import { compose403ErrorMessage, successHandler } from '../../../core/helpers/utils/sagaUtils';
import { BunkReaderScoreSettings, FarmParams } from './settings.types';

function* getSettingsManageSaga() {
  yield put(showLoader(LoadersNames.fetchSettings));
  try {
    const result: FarmParams = yield call(api.settings.getSettings);
    const {
      mixerChangesDefaultSaveCW,
      mixerChangesDefaultSaveDM,
      mixerChangesDefaultSaveHC,
      minimumSplitInclude,
      minimumSplitSave,
      optimizeLoadsMode,
      mixerChangesCWSaveMode,
      currency,
      rationEntryMethod,
      feedingSendMode,
      scaleWeightUnit,
      useZoneControl,
      hmsHCSystem,
      hmsMPSystem,
      hmsECSystem,
      hmsAutoRead,
      hmsHCImport,
      hmsMPImport,
      hmsECImport,
      premixAmountsByPercentage,
      dlType,
      dlExtFeedLineType,
      dlAutoSend,
      communicationInterval,
      communicationStartTime,
      communicationEndTime,
      dlExtFilePath,
      dlAutoReceiveMode,
      dlRadioComPort,
      dlRadioReserveChannel,
      dlRadioRadioType,
      dlLineByLine,
      dlIgnoreUnfinishedLoads,
      dlSupportOldIndicators,
      useMixerRoles,
      dlRadioReadsPerSecond,
      dlRadioDeletesPerSecond,
      dlRadioWaitSeconds,
      dlWifiConnectTimeout,
      dlWifiRetryTime,
      defaultDensity,
      refusalSubtractModeDefault,
      dlCommunicationStarted,
      useWeightMotionTolerance,
      timeShift,
      minimumLoadWeight,
      timeZone,
      hmsAgentActive,
      useLockedFeedings,
      automateShrTime,
      automateShrWeekday,
      automateShrMonthday,
      weighbridgeAutoRead,
      weighbridgeSystem,
      brAdjustmentType,
      brAutoProcess,
      brDefaultscore,
      brExpireTimeInHours,
      fsAdvancedSettings,
      fsMineralsInGrams,
      fsAutoReceive,
      fsAutoSend,
      fsInputFolder,
      fsInputFilename,
      fsOutputFolder,
      fsOutputFilename,
      fsSendStartTime,
      fsUseAgent,
      driverUsePermissions,
      driverRequirePIN,
      driverAutoLockScreen,
      driverAutoLockScreenDelay,
      unlimitedFeedings,
    } = result;

    yield put(
      actions.getSettingsManage.done({
        params: {},
        result: {
          driverChanges: {
            mixerChangesDefaultSaveCW,
            mixerChangesDefaultSaveDM,
            mixerChangesDefaultSaveHC,
            mixerChangesCWSaveMode,
          },
          loadOptimization: {
            minimumSplitInclude,
            minimumSplitSave,
            optimizeLoadsMode,
            feedingSendMode,
          },
          otherSettings: {
            rationEntryMethod,
            currency,
            scaleWeightUnit,
            useZoneControl,
            premixAmountsByPercentage,
            refusalSubtractModeDefault,
            useWeightMotionTolerance,
            timeShift,
            minimumLoadWeight,
            timeZone,
            useLockedFeedings,
          },
          agentSettings: {
            hmsHCSystem,
            hmsMPSystem,
            hmsECSystem,
            hmsAutoRead,
            hmsHCImport,
            hmsMPImport,
            hmsECImport,
            hmsAgentActive,
          },
          dataLinkSettings: {
            dlType,
            dlExtFeedLineType,
            dlAutoSend,
            communicationInterval,
            communicationStartTime,
            communicationEndTime,
            dlExtFilePath,
            dlAutoReceiveMode,
            dlRadioComPort,
            dlRadioReserveChannel,
            dlRadioRadioType,
            dlLineByLine,
            dlIgnoreUnfinishedLoads,
            dlSupportOldIndicators,
            useMixerRoles,
            dlRadioReadsPerSecond,
            dlRadioDeletesPerSecond,
            dlRadioWaitSeconds,
            dlWifiConnectTimeout,
            dlWifiRetryTime,
          },
          weighbridgeSettings: { weighbridgeAutoRead, weighbridgeSystem },
          inventorySettings: { automateShrTime, automateShrWeekday, automateShrMonthday },
          bunkReaderScoreSettings: { brAdjustmentType, brAutoProcess, brDefaultscore, brExpireTimeInHours },
          externalFeedingSystemsSettings: {
            fsAdvancedSettings,
            fsMineralsInGrams,
            fsAutoReceive,
            fsAutoSend,
            fsInputFolder: fsInputFolder || 'C:/Management',
            fsInputFilename: fsInputFilename || 'In.dat',
            fsOutputFolder: fsOutputFolder || 'C:/Management',
            fsOutputFilename: fsOutputFilename || 'Out.dat',
            fsSendStartTime,
            communicationInterval,
            fsUseAgent,
          },
          operatorManagementSettings: {
            driverUsePermissions,
            driverRequirePIN,
            driverAutoLockScreen,
            driverAutoLockScreenDelay,
          },
          defaultDensity,
          dlCommunicationStarted,
          unlimitedFeedings,
        },
      }),
    );

    if (!timeZone) {
      const timeZoneResult = yield call(api.settings.updateSettings, { timeZone: timeZoneConstant }, FormName.settings);

      yield put(
        actions.updateSettingsManage.done({
          params: {} as any,
          result: timeZoneResult,
        }),
      );
    }
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchSettings));
}

export function* updateSettingsManageSaga(action: ReturnType<typeof actions.updateSettingsManage.started>) {
  yield put(showLoader(LoadersNames.updateSettings));
  const { meta, payload } = action;
  const { data, formName } = payload;
  const showMessage = meta ? meta.showMessage : true;
  const onSuccess = meta && meta.onSuccess;
  const driverSettings = yield select(getFormValues(Forms.driverChanges)) || {};
  const loadsSettings = yield select(getFormValues(Forms.loadsProgramming)) || {};
  const otherSettings = yield select(getFormValues(Forms.otherSettings)) || {};
  const agentSettings = yield select(getFormValues(Forms.agentSettings)) || {};
  const datalinkSettings = yield select(getFormValues(Forms.datalinkSettings)) || {};
  const inventorySettings = yield select(getFormValues(Forms.inventorySettings)) || {};
  const weighBridgeSettings = yield select(getFormValues(Forms.weighBridgeSystem)) || {};
  const externalFeedingSystem = yield select(getFormValues(Forms.externalFeedingSystem)) || {};
  const operatormanagementSettings = yield select(getFormValues(Forms.operatorsManagement)) || {};

  const settings = {
    ...driverSettings,
    ...loadsSettings,
    ...otherSettings,
    ...agentSettings,
    ...datalinkSettings,
    ...inventorySettings,
    ...weighBridgeSettings,
    ...externalFeedingSystem,
    ...operatormanagementSettings,
  };

  try {
    const result = yield call(
      api.settings.updateSettings,
      !isEmpty(data) ? data : Object.keys(settings).length !== 0 ? settings : data,
      formName,
    );

    yield put(
      actions.updateSettingsManage.done({
        params: payload,
        result,
      }),
    );

    if (typeof onSuccess === 'function') {
      onSuccess();
    }

    if (showMessage) {
      yield put(notificationSuccess('settings:update_success'));
    }
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  // reset other settings form due to timeZone is changed indirect way.
  // this need to prevent showing modal window with confirmation of redirection
  yield put(reset(Forms.otherSettings));
  yield put(hideLoader(LoadersNames.updateSettings));
}

export function* getSettingsWithDelaySaga() {
  yield delay(300000);
  yield getSettingsManageSaga();
}

export function* updateDLCommunicationStartedSaga(
  action: ReturnType<typeof actions.updateDLCommunicationStarted.started>,
) {
  yield put(showLoader(LoadersNames.updateDLCommunicationStarted));
  try {
    const { value: dlCommunicationStarted, formName } = action.payload;
    const result = yield call(api.settings.updateSettings, { dlCommunicationStarted }, formName);

    yield put(
      actions.updateDLCommunicationStarted.done({
        params: action.payload,
        result: result.dlCommunicationStarted,
      }),
    );
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.updateDLCommunicationStarted));
}

export function* getWeighBridgeSystemsListSaga() {
  const hasPermission = yield call(checkPermissions, { resource: Resource.FEED_WEIGHBRIDGE, action: Action.READ });
  if (!hasPermission) return;
  yield put(showLoader(LoadersNames.fetchWeighBridgeSystems));
  try {
    const result = yield call(api.settings.getWeighBridgeSystem);
    yield put(
      actions.getWeighBridgeSystemsList.done({
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchWeighBridgeSystems));
}

export function* getThirdPartiesSaga() {
  const has3rdPartyPermission = yield call(checkPermissions, {
    resource: Resource.FEED_SETTINGS_3RD_PARTY,
    action: Action.READ,
  });
  const isOwnFarmSelected = yield select(farmsSelector.isOwnFarmSelected);

  if (!has3rdPartyPermission || !isOwnFarmSelected) {
    return;
  }

  yield put(showLoader(LoadersNames.fetchThirdParties));
  try {
    const result = yield call(api.settings.getThirdParties);
    yield put(
      actions.getThirdParties.done({
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchThirdParties));
}

export function* updateThirdPartySaga() {
  yield put(showLoader(LoadersNames.updateSettings));
  const formValues = yield select(getFormValues(Forms.thirdPartyIntegration));
  try {
    yield all(Object.entries(formValues).map(([id, value]) => call(updateThirdParty, id, value)));
    yield put(notificationSuccess('settings:update_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.updateSettings));
}

function* updateThirdParty(id, value) {
  try {
    const result = yield call(api.settings.updateThirdParties, id, { active: value });
    yield put(
      actions.updateThirdParty.done({
        result,
        params: {},
      }),
    );
  } catch (error) {}
}

function* getBunkScoreGrades() {
  try {
    const result = yield call(api.settings.getBunkScoreGrades);
    yield put(
      actions.getBunkScoreGrades.done({
        result,
      }),
    );
  } catch (error) {}
}

function* updateBunkScoreGrade(action: ReturnType<typeof actions.updateBunkScoreGrades.started>) {
  const { payload, meta } = action;
  try {
    const { brAdjustmentType, brAutoProcess, brDefaultscore, brExpireTimeInHours } = yield select(
      getFormValues(Forms.settingsForm),
    ) as Partial<BunkReaderScoreSettings>;

    yield call(api.settings.updateBunkScoreGrades, payload, FormName.settings);

    yield call(
      api.settings.updateSettings,
      {
        brAdjustmentType,
        brAutoProcess,
        brDefaultscore,
        brExpireTimeInHours,
      },
      FormName.settings,
    );

    successHandler(meta);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
}

export function* watchSettingsSaga() {
  yield all([
    takeLatest(getFarmsListRequest.done, getSettingsManageSaga),
    takeLatest(actions.getSettingsManage.started, getSettingsManageSaga),
    takeLatest(actions.updateSettingsManage.started, updateSettingsManageSaga),
    takeLatest(actions.getSettingsWithDelay.started, getSettingsWithDelaySaga),
    takeLatest(actions.updateDLCommunicationStarted.started, updateDLCommunicationStartedSaga),
    takeLatest(actions.getWeighBridgeSystemsList.started, getWeighBridgeSystemsListSaga),
    takeLatest(actions.getSettingsManage.done, getWeighBridgeSystemsListSaga),
    takeLatest(actions.getThirdParties.started, getThirdPartiesSaga),
    takeLatest(getFarmsListRequest.done, getThirdPartiesSaga),
    takeLatest(actions.updateThirdParty.started, updateThirdPartySaga),
    takeLatest(actions.getBunkScoreGrades.started, getBunkScoreGrades),
    takeLatest(actions.updateBunkScoreGrades.started, updateBunkScoreGrade),
  ]);
}
